/**
 * effects.ts — Short-lived visual flourishes: particle bursts, rings, decals.
 *
 * A tiny pooled particle system. Particles are reused rather than allocated per
 * spawn, so a burst costs nothing in GC pressure — important on mobile. Everything
 * is driven by an explicit `update(dt)` from the game loop rather than tweens, so
 * effects freeze cleanly when the run ends.
 *
 * Physics here is deliberately fake: particles get an initial velocity, fall under
 * a constant gravity, and fade out over their lifetime.
 */
import * as THREE from 'three';
import { EFFECTS } from '../config/constants';
import { progress01, easeOutCubic } from '../utils/easing';
import { createPoop } from './models';

interface Particle {
  mesh: THREE.Mesh;
  vx: number;
  vy: number; // vertical (world y)
  vz: number;
  spin: number;
  age: number;
  life: number;
  active: boolean;
}

interface Ring {
  mesh: THREE.Mesh;
  age: number;
  life: number;
  fromRadius: number;
  toRadius: number;
  active: boolean;
}

export class Effects {
  private group = new THREE.Group();
  private particles: Particle[] = [];
  private rings: Ring[] = [];
  private poop?: THREE.Object3D;
  /** Ring that tracks the dog while the player is cleaning up. */
  private progressRing?: THREE.Mesh;

  private particleGeo = new THREE.BoxGeometry(1, 1, 1);
  private ringGeo = new THREE.RingGeometry(0.9, 1, 24);

  constructor(private scene: THREE.Scene) {
    scene.add(this.group);
  }

  // ---- pooling ---------------------------------------------------------------

  private takeParticle(): Particle {
    let p = this.particles.find((q) => !q.active);
    if (!p) {
      if (this.particles.length >= EFFECTS.MAX_PARTICLES) {
        // Pool exhausted — recycle the oldest rather than growing without bound.
        p = this.particles.reduce((a, b) => (a.age > b.age ? a : b));
      } else {
        const mesh = new THREE.Mesh(
          this.particleGeo,
          new THREE.MeshLambertMaterial({ transparent: true }),
        );
        mesh.castShadow = false;
        p = { mesh, vx: 0, vy: 0, vz: 0, spin: 0, age: 0, life: 1, active: false };
        this.particles.push(p);
        this.group.add(mesh);
      }
    }
    p.active = true;
    p.age = 0;
    p.mesh.visible = true;
    return p;
  }

  private takeRing(): Ring {
    let r = this.rings.find((q) => !q.active);
    if (!r) {
      const mesh = new THREE.Mesh(
        this.ringGeo,
        new THREE.MeshBasicMaterial({ transparent: true, side: THREE.DoubleSide }),
      );
      mesh.rotation.x = -Math.PI / 2;
      r = { mesh, age: 0, life: 1, fromRadius: 1, toRadius: 2, active: false };
      this.rings.push(r);
      this.group.add(mesh);
    }
    r.active = true;
    r.age = 0;
    r.mesh.visible = true;
    return r;
  }

  // ---- public effects --------------------------------------------------------

  /**
   * Scatter `count` particles from a point on the ground.
   * @param spread horizontal speed range, @param lift initial upward speed
   */
  burst(
    x: number,
    y: number,
    opts: {
      count?: number;
      color?: number;
      size?: number;
      spread?: number;
      lift?: number;
      life?: number;
      height?: number;
    } = {},
  ): void {
    const {
      count = 12,
      color = 0xffffff,
      size = 3,
      spread = 60,
      lift = 90,
      life = 0.8,
      height = 12,
    } = opts;

    for (let i = 0; i < count; i++) {
      const p = this.takeParticle();
      const angle = Math.random() * Math.PI * 2;
      const speed = spread * (0.35 + Math.random() * 0.65);
      p.vx = Math.cos(angle) * speed;
      p.vz = Math.sin(angle) * speed;
      p.vy = lift * (0.5 + Math.random() * 0.8);
      p.spin = (Math.random() - 0.5) * 12;
      p.life = life * (0.7 + Math.random() * 0.6);
      const s = size * (0.6 + Math.random() * 0.8);
      p.mesh.scale.set(s, s, s);
      p.mesh.position.set(x, height, y);
      p.mesh.rotation.set(Math.random() * 3, Math.random() * 3, Math.random() * 3);
      (p.mesh.material as THREE.MeshLambertMaterial).color.setHex(color);
      (p.mesh.material as THREE.MeshLambertMaterial).opacity = 1;
    }
  }

  /** An expanding, fading ring on the ground — a soft "something happened here". */
  ring(x: number, y: number, color: number, toRadius = 60, life = 0.6): void {
    const r = this.takeRing();
    r.life = life;
    r.fromRadius = 6;
    r.toRadius = toRadius;
    r.mesh.position.set(x, EFFECTS.GROUND_CLEARANCE, y);
    (r.mesh.material as THREE.MeshBasicMaterial).color.setHex(color);
    (r.mesh.material as THREE.MeshBasicMaterial).opacity = 0.9;
  }

  /** Multi-coloured celebratory burst. */
  confetti(x: number, y: number): void {
    for (const color of EFFECTS.CONFETTI_COLORS) {
      this.burst(x, y, {
        count: 9,
        color,
        size: 4,
        spread: 110,
        lift: 190,
        life: 1.7,
        height: 40,
      });
    }
  }

  // ---- the poop ---------------------------------------------------------------

  /** Drop the clay poop swirl model at the dog's feet, with a little puff. */
  dropPoop(x: number, y: number): void {
    this.clearPoop();
    const model = createPoop();
    model.position.set(x, 0, y);
    this.poop = model;
    this.group.add(model);
    this.burst(x, y, { count: 8, color: 0xbba07a, size: 2.5, spread: 26, lift: 34, life: 0.5, height: 5 });
  }

  /** Remove the poop (on cleanup) with a sparkle. */
  cleanPoop(): void {
    if (!this.poop) return;
    const { x, z } = this.poop.position;
    this.burst(x, z, { count: 14, color: EFFECTS.SPARKLE_COLOR, size: 3, spread: 55, lift: 110, life: 0.7, height: 10 });
    this.ring(x, z, EFFECTS.SPARKLE_COLOR, 55, 0.5);
    this.clearPoop();
  }

  private clearPoop(): void {
    if (!this.poop) return;
    this.group.remove(this.poop);
    this.poop = undefined;
  }

  // ---- progress rings ----------------------------------------------------------

  /**
   * A ring around the dog that fills while it squats, in the poop's own brown.
   * `ratio` of 0 hides it.
   *
   * The only progress ring in the game now. There was a second one in
   * `EFFECTS.PROGRESS_COLOR` for the cleanup hold that followed it — the same
   * shape in the same place, seconds later — and the two read as one meter that
   * mysteriously restarted, which is part of why the hold is gone (`types.ts` →
   * `Objective`).
   */
  setPoopProgress(x: number, y: number, ratio: number): void {
    this.setProgressRing(x, y, ratio, EFFECTS.POOP_PROGRESS_COLOR);
  }

  private setProgressRing(x: number, y: number, ratio: number, color: number): void {
    if (ratio <= 0) {
      if (this.progressRing) this.progressRing.visible = false;
      return;
    }
    if (!this.progressRing) {
      this.progressRing = new THREE.Mesh(
        new THREE.RingGeometry(26, 32, 40, 1, -Math.PI / 2, Math.PI * 2),
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.95,
          side: THREE.DoubleSide,
        }),
      );
      this.progressRing.rotation.x = -Math.PI / 2;
      this.group.add(this.progressRing);
    }
    (this.progressRing.material as THREE.MeshBasicMaterial).color.setHex(color);
    this.progressRing.visible = true;
    // Above every ground surface, not just the tarmac: the pair are usually on
    // a raised footway while this is on screen, and a meter you can't see is
    // worse than no meter.
    this.progressRing.position.set(x, EFFECTS.GROUND_CLEARANCE, y);
    // Rebuild the arc so the ring sweeps as the hold fills.
    this.progressRing.geometry.dispose();
    this.progressRing.geometry = new THREE.RingGeometry(
      26,
      32,
      40,
      1,
      Math.PI / 2,
      -Math.PI * 2 * ratio,
    );
  }

  // ---- per-frame --------------------------------------------------------------

  update(dt: number): void {
    for (const p of this.particles) {
      if (!p.active) continue;
      p.age += dt;
      if (p.age >= p.life) {
        p.active = false;
        p.mesh.visible = false;
        continue;
      }
      p.vy -= EFFECTS.GRAVITY * dt;
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.y += p.vy * dt;
      p.mesh.position.z += p.vz * dt;
      // Bounce softly off the ground instead of sinking through it.
      if (p.mesh.position.y < 1.5) {
        p.mesh.position.y = 1.5;
        p.vy *= -0.35;
        p.vx *= 0.7;
        p.vz *= 0.7;
      }
      p.mesh.rotation.x += p.spin * dt;
      p.mesh.rotation.z += p.spin * dt * 0.7;
      (p.mesh.material as THREE.MeshLambertMaterial).opacity =
        1 - progress01(p.age, p.life) ** 2;
    }

    for (const r of this.rings) {
      if (!r.active) continue;
      r.age += dt;
      if (r.age >= r.life) {
        r.active = false;
        r.mesh.visible = false;
        continue;
      }
      const t = easeOutCubic(progress01(r.age, r.life));
      const radius = r.fromRadius + (r.toRadius - r.fromRadius) * t;
      r.mesh.scale.set(radius, radius, radius);
      (r.mesh.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - t);
    }
  }

  /** Reset all transient effects (used when a run restarts). */
  clear(): void {
    for (const p of this.particles) {
      p.active = false;
      p.mesh.visible = false;
    }
    for (const r of this.rings) {
      r.active = false;
      r.mesh.visible = false;
    }
    this.clearPoop();
    if (this.progressRing) this.progressRing.visible = false;
  }

  dispose(): void {
    this.clear();
    this.scene.remove(this.group);
    this.particleGeo.dispose();
    this.ringGeo.dispose();
  }
}
